import { addDays, endOfDay, startOfWeek } from 'date-fns';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, CalendarDeleteDate, Flag } from 'shared/assets/icons';
import { Datepicker } from 'shared/components/ui/datepicker';
import {
  DatePresets,
  IconOptions,
} from 'shared/components/ui/datepicker-with-options/types';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { useDateInput } from 'shared/hooks/use-date-input';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { usePopupMenuArrowNavigation } from 'shared/hooks/use-popup-menu-arrow-navigation';
import { getNextWeekday } from 'shared/lib/date-fns';
import {
  DateFormatOptions,
  dateFormatOptionToLabelMap,
} from 'shared/types/date-format-options';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { parseDateInput } from 'shared/utils/parse-date-input';

const fridayWeekdayNumber = 5;

const defaultDatePresets = [
  DatePresets.Today,
  DatePresets.Tomorrow,
  DatePresets.NextWeek,
];

const presetLabelMap: Record<DatePresets, string> = {
  [DatePresets.Today]: 'forms.deadline.fields.today.label',
  [DatePresets.Tomorrow]: 'forms.deadline.fields.tomorrow.label',
  [DatePresets.Friday]: 'forms.deadline.fields.friday.label',
  [DatePresets.NextWeek]: 'forms.deadline.fields.next-week.label',
};

export type DatepickerWithOptionsProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children'
> & {
  value: Timestamp | undefined;
  onChange: (date?: Timestamp) => void;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  datePresets?: DatePresets[];
  icon?: IconOptions;
  canRemoveValue?: boolean;
  forceRemoveValueOption?: boolean;
  removeLabel?: string;
  shortcuts?: Partial<Record<DatePresets, string[]>>;
  clearShortcut?: string[];
};

export const DatepickerWithOptions: React.FC<DatepickerWithOptionsProps> = ({
  value,
  onChange,
  dateFormat,
  weekStartsOn,
  datePresets = defaultDatePresets,
  icon = IconOptions.Calendar,
  canRemoveValue = true,
  forceRemoveValueOption,
  removeLabel,
  shortcuts,
  clearShortcut,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const today = useToday();
  const { t } = useTranslation();
  const {
    value: inputValue,
    onChange: onMaskedChange,
    onKeyDown: onDateInputKeyDown,
    reset: resetInput,
  } = useDateInput(value, dateFormat);

  const [inputError, setInputError] = useState(false);

  // The calendar follows a valid typed date before it is committed, so Enter is never a surprise.
  const previewDate = useMemo(
    () => parseDateInput(inputValue, dateFormat, today),
    [inputValue, dateFormat, today],
  );

  const hasRemoveValueOption =
    !!(forceRemoveValueOption || value) && canRemoveValue;

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    onMaskedChange(e);
    setInputError(false);
  };

  const clearInput = () => {
    resetInput();
    setInputError(false);
    inputRef.current?.focus();
  };

  // Every commit re-seeds the input, so the typed text and the calendar can never disagree.
  const commitDate = (date?: Timestamp) => {
    resetInput(date);
    setInputError(false);
    onChange(date);
  };

  const changeToPreset = (preset: DatePresets) => {
    switch (preset) {
      case DatePresets.Today:
        commitDate(endOfDay(new Date()));
        break;
      case DatePresets.Tomorrow:
        commitDate(endOfDay(addDays(new Date(), 1)));
        break;
      case DatePresets.Friday:
        commitDate(endOfDay(getNextWeekday(new Date(), fridayWeekdayNumber)));
        break;
      case DatePresets.NextWeek:
        commitDate(
          endOfDay(
            startOfWeek(addDays(new Date(), 7), {
              weekStartsOn: weekdaysNumberMap[weekStartsOn],
            }),
          ),
        );
        break;
      default:
        break;
    }
  };

  const clear = () => commitDate(undefined);

  const onChangeDate = (date: Date | null) =>
    commitDate(date ? endOfDay(date) : undefined);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onDateInputKeyDown(e);

    if (!e.shiftKey) {
      return;
    }
    switch (e.code) {
      case 'KeyT': {
        e.preventDefault();
        changeToPreset(e.altKey ? DatePresets.Tomorrow : DatePresets.Today);
        break;
      }
      case 'KeyW': {
        e.preventDefault();
        changeToPreset(DatePresets.NextWeek);
        break;
      }
      case 'KeyU': {
        e.preventDefault();
        clear();
        break;
      }
      default:
        break;
    }
  };

  useKeysDown(
    ['Enter'],
    () => {
      if (!inputRef.current || document.activeElement !== inputRef.current) {
        return;
      }

      if (!inputRef.current.value) {
        commitDate(undefined);
        return;
      }

      if (previewDate) {
        commitDate(previewDate);
        return;
      }

      setInputError(true);
    },
    { otherKeysInCombinationAllowed: true },
  );

  usePopupMenuArrowNavigation({
    containerRef,
    inputRef,
    gridSelector: '.react-datepicker',
  });

  return (
    <PopupMenu {...rest} ref={containerRef} initialFocus={inputRef}>
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuInput
            ref={inputRef}
            iconColor={inputError ? Colors.Error : undefined}
            startIcon={icon === IconOptions.Calendar ? Calendar : Flag}
            textColor={inputError ? Colors.Error : undefined}
            value={inputValue}
            onChange={onChangeInput}
            onKeyDown={handleKeyDown}
            onClear={inputValue ? clearInput : undefined}
            placeholder={dateFormatOptionToLabelMap[dateFormat]}
          />
        </PopupMenuListItem>
      </PopupMenuList>

      {!!datePresets.length && (
        <PopupMenuList hasBottomBorder>
          {datePresets.map((preset) => {
            const _onClick = () => changeToPreset(preset);

            return (
              <PopupMenuListItem key={preset}>
                <PopupMenuButton
                  onClick={_onClick}
                  start={
                    <Icon
                      icon={icon === IconOptions.Calendar ? Calendar : Flag}
                    />
                  }
                  tabIndex={0}
                  type="button"
                  shortcut={shortcuts?.[preset]}
                >
                  {t(presetLabelMap[preset])}
                </PopupMenuButton>
              </PopupMenuListItem>
            );
          })}
        </PopupMenuList>
      )}
      {hasRemoveValueOption && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={clear}
              start={<Icon icon={CalendarDeleteDate} />}
              tabIndex={0}
              type="button"
              shortcut={clearShortcut}
            >
              {removeLabel ?? t('forms.deadline.fields.clear.label')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      <PopupMenuList>
        <Datepicker
          today={today}
          selected={previewDate ?? value}
          onChange={onChangeDate}
          dateFormat={dateFormat}
          weekStartsOn={weekStartsOn}
          inline
        />
      </PopupMenuList>
    </PopupMenu>
  );
};
